import React, { FC, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../../../components/common/Modal'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import SearchResult from '../../search/SearchResult'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { URL } from '../../../constants/url'
import { setPath, setUrlParam } from '../../../../utils/url'

interface SearchValues {
  startDate: string
  endDate: string
  teamIds?: number[]
  teamNames?: string[]
  memberIds?: number[]
  memberNames?: string[]
  keywords?: string[]
  serviceNames?: string[]
  categoryIds?: number[]
  categoryNames?: string[]
  efficiencyLevel?: string[]
  stressLevel?: string[]
  focusLevel?: string[]
  lowerLimitTime?: number
  sortKey?: string,
  sortType?: string
}

interface Props {
  type?: string
  target?: string
  searchValue: SearchValues
  isDisplay: boolean
  closeCallback: () => void
}

type LangValue = 'ja' | 'en'

const SearchModal: FC<Props> = (props) => {
  const {
    type,
    target,
    searchValue,
    isDisplay,
    closeCallback,
  } = props

  const { language } = useParams<{ language: LangValue }>()
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()

  const {
    search,
    login,
  } = useSelector((state: RootState) => (state))

  const searchRef = useRef<HTMLDivElement>(null)

  const efficiencyScoreList = [
    {
      name: t('search.conditions.score.efficiency_a'),
      value: 'a',
    },
    {
      name: t('search.conditions.score.efficiency_c'),
      value: 'c',
    },
  ]

  const stressScoreList = [
    {
      name: t('search.conditions.score.stress_a'),
      value: 'a',
    },
    {
      name: t('search.conditions.score.stress_c'),
      value: 'c',
    },
  ]

  const focusScoreList = [
    {
      name: t('search.conditions.score.focus_a'),
      value: 'a',
    },
    {
      name: t('search.conditions.score.focus_c'),
      value: 'c',
    },
  ]

  const closeModal = () => {
    closeCallback()
  }

  const moveSearchPage = () => {
    closeCallback()
    const array = []
    if (searchValue.startDate) {
      array.push(`start_date=${searchValue.startDate}`)
    }
    if (searchValue.endDate) {
      array.push(`end_date=${searchValue.endDate}`)
    }
    if (searchValue.teamIds) {
      array.push(`team_ids=${searchValue.teamIds.join(',')}`)
    }
    if (searchValue.memberIds) {
      array.push(`member_ids=${searchValue.memberIds.join(',')}`)
    }
    if (searchValue.keywords) {
      array.push(`keywords=${searchValue.keywords.join(',')}`)
    }
    if (searchValue.serviceNames) {
      array.push(`service_names=${searchValue.serviceNames.join(',')}`)
    }
    if (searchValue.categoryIds) {
      array.push(`category_ids=${searchValue.categoryIds.join(',')}`)
    }
    if (searchValue.efficiencyLevel) {
      array.push(`efficiency=${searchValue.efficiencyLevel.join(',')}`)
    }
    if (searchValue.stressLevel) {
      array.push(`stress=${searchValue.stressLevel.join(',')}`)
    }
    if (searchValue.focusLevel) {
      array.push(`focus=${searchValue.focusLevel.join(',')}`)
    }
    if (searchValue.lowerLimitTime) {
      array.push(`lower_limit_time=${searchValue.lowerLimitTime}`)
    }
    if (searchValue.sortKey) {
      array.push(`sort_key=${searchValue.sortKey}`)
    }
    if (searchValue.sortType) {
      array.push(`sort_type=${searchValue.sortType}`)
    }
    history.push(`${setPath(URL.SEARCH.path, language)}?${array.join('&')}`, { referrer: `${location.pathname}${location.search}` })
  }

  const moveReportPage = () => {
    closeCallback()
    if (!searchValue.memberIds) return
    if (type === "weekly") {
      history.push(`${setPath(setUrlParam(
        URL.REPORT_DETAIL_WEEKLY.path,
        [
          { key: 'target', value: "member" },
          { key: 'id', value: searchValue.memberIds[0].toString() },
        ],
      ), language)}?start_date=${searchValue.startDate}`, { referrer: `${location.pathname}${location.search}` })
    } else if (type === "monthly") {
      history.push(`${setPath(setUrlParam(
        URL.REPORT_DETAIL_MONTHLY.path,
        [
          { key: 'target', value: "member" },
          { key: 'id', value: searchValue.memberIds[0].toString() },
        ],
      ), language)}?start_date=${searchValue.startDate}`, { referrer: `${location.pathname}${location.search}` })
    }
  }

  return (
    <Modal
      title="作業一覧"
      show={isDisplay}
      wrapperClass="report_modal"
      closeCallback={closeModal}
      closeable
    >
      <section className="search_page">
        <div
          style={{
            position: "absolute",
            top: "30px",
            right: "30px",
          }}
        >
          {
            type && searchValue.memberNames?.length && target === "team" ? (
              <button
                type="button"
                style={{
                  border: "solid 1px #CCC",
                  borderRadius: "4px",
                  marginRight: "16px",
                  backgroundColor: "#4d85d9",
                  color: "#FFF",
                }}
                onClick={() => moveReportPage()}
              >
                {searchValue.memberNames[0]}
                さんのレポートを見る
              </button>
            ) : (
              <></>
            )
          }
          <button
            type="button"
            style={{
              border: "solid 1px #CCC",
              borderRadius: "4px",
            }}
            onClick={() => moveSearchPage()}
          >
            作業一覧ページに移動する
          </button>
        </div>
        <section
          ref={searchRef}
          style={{
            backgroundColor: "#FFF",
            borderRadius: "4px",
            padding: "32px",
            marginBottom: "40px",
            display: "flex",
            flexFlow: "wrap",
            alignItems: "center",
            fontSize: "12px",
            color: "#999",
            position: "absolute",
            left: "0",
            top: "86px",
            zIndex: "2",
            width: "100%",
          }}
        >
          <div className="mb8">
            対象期間:
            <div className="chip">
              {
                searchValue.startDate === searchValue.endDate ? (
                  <>
                    {searchValue.startDate}
                  </>
                ) : (
                  <>
                    {searchValue.startDate}
                    ~
                    {searchValue.endDate}
                  </>
                )
              }
            </div>
          </div>
          {
            (!!searchValue.teamNames?.length || !!searchValue.memberNames?.length) && (
              <div className="mb8">
                チーム/ユーザー:
                {
                  searchValue.teamNames?.map((teamName) => (
                    <div
                      className="no_trans chip"
                      key={`team_${teamName}`}
                    >
                      {teamName}
                    </div>
                  ))
                }
                {
                  searchValue.memberNames?.map((memberName) => (
                    <div
                      className="no_trans chip"
                      key={`member_${memberName}`}
                    >
                      {memberName}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            (!!searchValue.categoryNames?.length || !!searchValue.serviceNames?.length) && (
              <div className="mb8">
                分類/アプリ:
                {
                  searchValue.categoryNames?.map((categoryName) => (
                    <div
                      className="chip"
                      key={`category_${categoryName}`}
                    >
                      {categoryName}
                    </div>
                  ))
                }
                {
                  searchValue.serviceNames?.map((serviceName) => (
                    <div
                      className="chip"
                      key={`service_${serviceName}`}
                    >
                      {serviceName}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.keywords?.length && (
              <div className="mb8">
                フリーキーワード:
                {
                  searchValue.keywords.map((keyword) => (
                    <div
                      className="no_trans chip"
                      key={`keyword_${keyword}`}
                    >
                      {keyword}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.efficiencyLevel?.length && (
              <div className="mb8">
                業務効率適正度:
                {
                  searchValue.efficiencyLevel.map((efficiency) => (
                    <div
                      className="chip"
                      key={`efficiency_${efficiency}`}
                    >
                      {efficiencyScoreList.find((score) => score.value === efficiency)?.name || ""}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.stressLevel?.length && (
              <div className="mb8">
                業務負荷適正度:
                {
                  searchValue.stressLevel.map((stress) => (
                    <div
                      className="chip"
                      key={`stress_${stress}`}
                    >
                      {stressScoreList.find((score) => score.value === stress)?.name || ""}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.focusLevel?.length && (
              <div className="mb8">
                業務集中適正度:
                {
                  searchValue.focusLevel.map((focus) => (
                    <div
                      className="chip"
                      key={`focus_${focus}`}
                    >
                      {focusScoreList.find((score) => score.value === focus)?.name || ""}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            !!searchValue.lowerLimitTime && (
              <div className="mb8">
                5分以上の作業のみ表示する:
                <div
                  className="chip"
                  key={`lower_limit_time_${searchValue.lowerLimitTime}`}
                >
                  オン
                </div>
              </div>
            )
          }
          {
            !!searchValue.sortKey?.length && (
              <div className="mb8">
                並び替え:
                <div
                  className="chip"
                  key={`sort_type_${searchValue.sortKey}`}
                >
                  {t(`search.result.data.sort.${searchValue.sortType}.${searchValue.sortKey}`)}
                </div>
              </div>
            )
          }
        </section>
        <div
          style={{
            marginTop: `${searchRef.current?.clientHeight || 120}px`,
          }}
        />
        <SearchResult
          searchValue={{
            start_date: searchValue.startDate,
            end_date: searchValue.endDate,
            team_names: searchValue.teamNames,
            member_names: searchValue.memberNames,
            keywords: searchValue.keywords,
            service_names: searchValue.serviceNames,
            category_names: searchValue.categoryNames,
            efficiency_level: searchValue.efficiencyLevel,
            stress_level: searchValue.stressLevel,
            focus_level: searchValue.focusLevel,
            sort_key: searchValue.sortKey,
            sort_type: searchValue.sortType,
          }}
          searchResult={search.searchResult}
          isLoading={search.isLoading}
          displayGraph={login.function_enabled_state?.ai_work_assessment || false}
        />
      </section>
    </Modal>
  )
}

SearchModal.defaultProps = {
  type: undefined,
  target: undefined,
}

export default SearchModal
